/*
 * FileManager
 *
 * Manages file operations for image cropping, including creating File
 * objects from blobs, restoring files from data URLs, and handling
 * form submission.
 *
 * Usage:
 *   const manager = new FileManager(errorHandler, imageProcessor, domManager)
 *   const file = manager.createFile(blob, "image.png", "image/png")
 *   await manager.restoreFileFromDataUrl(dataUrl, inputElement, "restored.png")
 */
export class FileManager {
  /*
   * constructor
   *
   * Creates a new FileManager instance.
   *
   * Inputs:
   *   errorHandler   {ErrorHandler}   - Error handler for reporting errors.
   *   imageProcessor {ImageProcessor} - Image processor for conversions.
   *   domManager     {DomManager}     - DOM manager for element updates.
   */
  constructor(errorHandler, imageProcessor, domManager) {
    this.errorHandler = errorHandler
    this.imageProcessor = imageProcessor
    this.domManager = domManager
  }

  /*
   * createFile
   *
   * Creates a File object from a Blob.
   *
   * Inputs:
   *   blob     {Blob}   - The blob to wrap in a File.
   *   filename {string} - The filename for the File.
   *   mimeType {string} - The MIME type. Default: "image/png".
   *
   * Output:
   *   {File} - The created File object.
   */
  createFile(blob, filename, mimeType = "image/png") {
    return new File([blob], filename, {
      type: mimeType,
      lastModified: Date.now()
    })
  }

  /*
   * fetchBlobFromDataUrl
   *
   * Fetches a blob from a data URL.
   *
   * Input:
   *   dataUrl {string} - The data URL to fetch.
   *
   * Output:
   *   {Promise<Blob>} - The fetched blob.
   */
  async fetchBlobFromDataUrl(dataUrl) {
    const response = await fetch(dataUrl)
    if (!response.ok) {
      throw new Error(`Fetch failed with status ${response.status}`)
    }
    return response.blob()
  }

  /*
   * restoreFileFromDataUrl
   *
   * Restores a file input's value by fetching a data URL and converting
   * it to a File.
   *
   * Inputs:
   *   dataUrl      {string}           - The data URL to restore from.
   *   inputElement {HTMLInputElement} - The file input to restore.
   *   filename     {string}           - The filename to use.
   *   mimeType     {string}           - The MIME type. Default: "image/png".
   *
   * Output:
   *   {Promise<File>} - The created File object.
   */
  async restoreFileFromDataUrl(dataUrl, inputElement, filename, mimeType = "image/png") {
    const blob = await this.fetchBlobFromDataUrl(dataUrl)
    const file = this.createFile(blob, filename, mimeType)
    this.domManager.setFileInput(inputElement, file)
    return file
  }

  /*
   * isValidImageUrl
   *
   * Tests whether a string looks like a valid image source URL.
   *
   * Input:
   *   src {*} - The value to test.
   *
   * Output:
   *   {boolean} - True if the value appears to be a loadable image URL.
   */
  isValidImageUrl(src) {
    if (!src || typeof src !== "string") return false
    return (
      src.startsWith("http:") ||
      src.startsWith("https:") ||
      src.startsWith("data:") ||
      (src.startsWith("/") && src.length > 1) ||
      src.startsWith("blob:")
    )
  }

  /*
   * getMimeType
   *
   * Returns the MIME type string for a format identifier.
   *
   * Input:
   *   format {string} - Format identifier: "png" or "jpeg".
   *
   * Output:
   *   {string} - MIME type string.
   */
  getMimeType(format) {
    return format === "jpeg" ? "image/jpeg" : "image/png"
  }

  /*
   * getFileExtension
   *
   * Returns the file extension for a format identifier.
   *
   * Input:
   *   format {string} - Format identifier: "png" or "jpeg".
   *
   * Output:
   *   {string} - File extension including dot.
   */
  getFileExtension(format) {
    return format === "jpeg" ? ".jpg" : ".png"
  }
}
